import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, SectionLabel } from "@/components/Primitives";
import { Loader2, Megaphone, CheckCircle2, XCircle, Plus } from "lucide-react";
import { toast } from "sonner";

const RADII = [0, 250, 500, 1000];
const WINDOWS = [7, 30, 60, 90];

function toLocalDatetimeInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CampaignBuilder() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const prefill = state?.prefill;

  const today = new Date();
  const endDefault = new Date();
  endDefault.setDate(today.getDate() + 14);

  const [form, setForm] = useState({
    name: prefill?.label ? `${prefill.label} — Activation` : "",
    advertiser: "",
    objective: "awareness",
    budget_usd: 5000,
    flight_start: toLocalDatetimeInput(today),
    flight_end: toLocalDatetimeInput(endDefault),
    creative_url: "",
    latitude: prefill?.latitude ?? 40.7128,
    longitude: prefill?.longitude ?? -74.006,
    radius_m: prefill?.radius_m ?? 1000,
    time_window_days: prefill?.time_window_days ?? 30,
    audience_count: prefill?.audience_count ?? 0,
    audience_result_id: prefill?.audience_result_id ?? null,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [advertisers, setAdvertisers] = useState([]);
  const [showAddAdv, setShowAddAdv] = useState(false);
  const [newAdvName, setNewAdvName] = useState("");
  const [savingAdv, setSavingAdv] = useState(false);

  const loadAdvertisers = async () => {
    try {
      const { data } = await api.get("/advertisers/");
      setAdvertisers(data);
    } catch { /* noop */ }
  };

  useEffect(() => { loadAdvertisers(); }, []);

  const addAdvertiserInline = async () => {
    const name = newAdvName.trim();
    if (!name) return;
    setSavingAdv(true);
    try {
      const { data } = await api.post("/advertisers/", { name });
      setAdvertisers((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((f) => ({ ...f, advertiser_id: data.id }));
      setNewAdvName("");
      setShowAddAdv(false);
      toast.success("Advertiser added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add");
    } finally {
      setSavingAdv(false);
    }
  };

  // If the user changes radius/window and we have a prefilled audience_result, recompute count
  const [resultCells, setResultCells] = useState(null);
  useEffect(() => {
    if (!form.audience_result_id) return;
    let abort = false;
    (async () => {
      try {
        const { data } = await api.get(`/audience/results/${form.audience_result_id}`);
        if (!abort) setResultCells(data.cells);
      } catch {/* noop */}
    })();
    return () => { abort = true; };
  }, [form.audience_result_id]);

  useEffect(() => {
    if (!resultCells) return;
    const c = resultCells.find(x => x.radius_m === Number(form.radius_m) && x.time_window_days === Number(form.time_window_days));
    if (c) setForm(f => ({ ...f, audience_count: c.count }));
  }, [form.radius_m, form.time_window_days, resultCells]);

  const advertiserName = useMemo(() => {
    return advertisers.find((a) => a.id === form.advertiser_id)?.name || "";
  }, [advertisers, form.advertiser_id]);

  const payload = useMemo(() => ({
    name: form.name,
    advertiser: advertiserName,
    advertiser_id: form.advertiser_id || null,
    objective: form.objective,
    budget_usd: Number(form.budget_usd),
    flight: {
      start: new Date(form.flight_start).toISOString(),
      end: new Date(form.flight_end).toISOString(),
    },
    creative: form.creative_url ? { url: form.creative_url } : null,
    targeting: {
      geo: {
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        radius_m: Number(form.radius_m),
      },
      audience_window_days: Number(form.time_window_days),
      audience_size: Number(form.audience_count),
    },
    audience_reference: form.audience_result_id,
    notes: form.notes || null,
  }), [form, advertiserName]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.advertiser_id) {
      setError("Please select an advertiser.");
      return;
    }
    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      const body = {
        name: form.name,
        advertiser: advertiserName,
        advertiser_id: form.advertiser_id,
        objective: form.objective,
        budget_usd: Number(form.budget_usd),
        flight_start: new Date(form.flight_start).toISOString(),
        flight_end: new Date(form.flight_end).toISOString(),
        creative_url: form.creative_url || null,
        targeting: {
          latitude: Number(form.latitude),
          longitude: Number(form.longitude),
          radius_m: Number(form.radius_m),
          time_window_days: Number(form.time_window_days),
          audience_count: Number(form.audience_count),
        },
        audience_result_id: form.audience_result_id,
        notes: form.notes || null,
      };
      const { data } = await api.post("/campaigns/push", body);
      setResult(data);
      if (data.push_status === "accepted") {
        toast.success(`Campaign accepted · ${data.external_campaign_id}`);
      } else {
        toast.error("Campaign rejected by external platform");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1500px]">
      <PageHeader
        eyebrow="Module / 04"
        title="Campaign Builder"
        description="Configure a campaign, watch the JSON payload assemble in real time, and push it to the external campaign platform."
        testid="campaign-builder-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-7 space-y-6" data-testid="campaign-form">
          <div className="bg-white border border-[#E0E0D8] p-6">
            <SectionLabel>Campaign meta</SectionLabel>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required>
                <input data-testid="campaign-name-input" required value={form.name} onChange={e => set("name", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Advertiser" required>
                <div className="flex gap-2">
                  <select
                    data-testid="campaign-advertiser-select"
                    required
                    value={form.advertiser_id}
                    onChange={(e) => set("advertiser_id", e.target.value)}
                    className={inputCls + " flex-1"}
                  >
                    <option value="">Select advertiser…</option>
                    {advertisers.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowAddAdv((v) => !v)}
                    data-testid="campaign-add-advertiser-toggle"
                    className="border border-[#0A0A0A] px-3 hover:bg-[#0A0A0A] hover:text-white transition-colors"
                    title="Add advertiser"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                {showAddAdv && (
                  <div className="mt-2 flex gap-2">
                    <input
                      data-testid="campaign-new-advertiser-input"
                      autoFocus
                      value={newAdvName}
                      onChange={(e) => setNewAdvName(e.target.value)}
                      placeholder="New advertiser name"
                      className={inputCls + " flex-1"}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAdvertiserInline(); } }}
                    />
                    <button
                      type="button" onClick={addAdvertiserInline} disabled={savingAdv}
                      data-testid="campaign-new-advertiser-save"
                      className="bg-[#FF3E00] text-white px-3 text-xs font-mono uppercase tracking-wider disabled:opacity-60"
                    >
                      {savingAdv ? "…" : "Save"}
                    </button>
                  </div>
                )}
                {advertisers.length === 0 && !showAddAdv && (
                  <p className="text-[10px] font-mono uppercase tracking-wider text-[#888] mt-1">
                    No advertisers yet — <Link to="/advertisers" className="text-[#FF3E00] hover:underline">manage advertisers</Link> or click <Plus size={10} className="inline -mt-0.5" /> to add inline.
                  </p>
                )}
              </Field>
              <Field label="Objective">
                <select data-testid="campaign-objective-select" value={form.objective} onChange={e => set("objective", e.target.value)} className={inputCls}>
                  <option value="awareness">Awareness</option>
                  <option value="consideration">Consideration</option>
                  <option value="conversion">Conversion</option>
                </select>
              </Field>
              <Field label="Budget (USD)">
                <input data-testid="campaign-budget-input" type="number" min="1" step="0.01" value={form.budget_usd} onChange={e => set("budget_usd", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Flight start">
                <input data-testid="campaign-flight-start-input" type="datetime-local" value={form.flight_start} onChange={e => set("flight_start", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Flight end">
                <input data-testid="campaign-flight-end-input" type="datetime-local" value={form.flight_end} onChange={e => set("flight_end", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Creative URL (optional)" full>
                <input data-testid="campaign-creative-input" type="url" placeholder="https://" value={form.creative_url} onChange={e => set("creative_url", e.target.value)} className={inputCls} />
              </Field>
            </div>
          </div>

          <div className="bg-white border border-[#E0E0D8] p-6">
            <SectionLabel>Targeting</SectionLabel>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Latitude">
                <input data-testid="campaign-lat-input" type="number" step="any" value={form.latitude} onChange={e => set("latitude", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Longitude">
                <input data-testid="campaign-lon-input" type="number" step="any" value={form.longitude} onChange={e => set("longitude", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Radius">
                <select data-testid="campaign-radius-select" value={form.radius_m} onChange={e => set("radius_m", Number(e.target.value))} className={inputCls}>
                  {RADII.map(r => <option key={r} value={r}>{r === 0 ? "Exact" : `${r} m`}</option>)}
                </select>
              </Field>
              <Field label="Time window">
                <select data-testid="campaign-window-select" value={form.time_window_days} onChange={e => set("time_window_days", Number(e.target.value))} className={inputCls}>
                  {WINDOWS.map(w => <option key={w} value={w}>Last {w} days</option>)}
                </select>
              </Field>
              <Field label="Audience size" full>
                <input data-testid="campaign-audience-size-input" type="number" min="0" value={form.audience_count} onChange={e => set("audience_count", e.target.value)} className={inputCls} />
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#888] mt-1">
                  {resultCells ? "Auto-synced from selected audience result" : "Manually entered"}
                </p>
              </Field>
              <Field label="Notes" full>
                <textarea data-testid="campaign-notes-input" rows={3} value={form.notes} onChange={e => set("notes", e.target.value)} className={inputCls} />
              </Field>
            </div>
          </div>

          {error && (
            <p data-testid="campaign-error" className="text-xs font-mono text-[#D91919] border border-[#D91919]/30 bg-[#D91919]/5 px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-3">
            <button type="submit" disabled={submitting} data-testid="campaign-push-button"
              className="bg-[#FF3E00] text-white px-6 py-3 font-semibold hover:bg-[#D93500] transition-colors flex items-center gap-2 disabled:opacity-60">
              {submitting ? <Loader2 className="animate-spin" size={16}/> : <Megaphone size={16}/>}
              {submitting ? "Pushing…" : "Push campaign"}
            </button>
            {result?.push_status === "accepted" && (
              <button type="button" onClick={() => navigate(`/campaigns/${result.id}`)}
                className="border border-[#0A0A0A] px-6 py-3 font-semibold hover:bg-[#0A0A0A] hover:text-white transition-colors">
                Open campaign
              </button>
            )}
          </div>
        </form>

        {/* JSON preview + result */}
        <aside className="lg:col-span-5 space-y-4 lg:sticky lg:top-6 self-start">
          <div>
            <SectionLabel>Live payload preview</SectionLabel>
            <pre data-testid="json-preview"
              className="bg-[#0A0A0A] text-[#E0E0D8] font-mono p-5 border border-[#E0E0D8] overflow-x-auto text-[11px] leading-relaxed max-h-[500px]">
{JSON.stringify(payload, null, 2)}
            </pre>
          </div>

          {result && (
            <div className={`border-2 ${result.push_status === "accepted" ? "border-[#00994D]" : "border-[#D91919]"} bg-white p-5`} data-testid="campaign-result">
              <div className="flex items-center gap-2 mb-3">
                {result.push_status === "accepted"
                  ? <CheckCircle2 className="text-[#00994D]" size={18}/>
                  : <XCircle className="text-[#D91919]" size={18}/>}
                <p className={`text-xs font-mono uppercase tracking-[0.2em] ${result.push_status === "accepted" ? "text-[#00994D]" : "text-[#D91919]"}`}>
                  {result.push_status}
                </p>
              </div>
              {result.external_campaign_id && (
                <p className="font-mono text-sm">
                  <span className="text-[#555]">EXT_ID</span> ·{" "}
                  <span data-testid="external-campaign-id">{result.external_campaign_id}</span>
                </p>
              )}
              <pre className="mt-3 font-mono text-[11px] text-[#555] bg-[#F4F4F0] p-3 overflow-x-auto">
{JSON.stringify(result.push_response, null, 2)}
              </pre>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none";

function Field({ label, children, required, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[11px] font-mono uppercase tracking-[0.15em] text-[#555] mb-1">
        {label}{required && <span className="text-[#FF3E00]"> *</span>}
      </label>
      {children}
    </div>
  );
}
